import { useContext, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import {
  QUESTION_LOGIC_OPERATORS,
  QUESTION_TYPES,
  QUESTION_TYPES_CAN_DISQUALIFY,
  SURVEY_TYPES,
} from '@api/instant_survey';
import { getQuestionTypeIcon } from '@helpers';

import {
  ActionOverlayPopover,
  Card,
  ColumnsIcon,
  CopyIcon,
  DragDropV2Icon,
  FlowArrowIcon,
  Layout,
  PencilIcon,
  Pill,
  RowsIcon,
  TargetIcon,
  TrashIcon,
} from '@utilities';

import GridAnnouncement from '../../../ModalInstantSurveyQuestion/components/GridAnnouncement';
import MaxDiffAnnouncement from '../../../ModalInstantSurveyQuestion/components/MaxDiffAnnouncement';
import QuestionLogic from '../../../QuestionLogic';
import QuestionTypePill from '../QuestionTypePill';
import { SurveyContext } from '../../../../../../utilities/context';
import { getNumericTypeText } from '../../../../../../utilities/helpers';

import styles from './_index.module.scss';

const answerOperators = [QUESTION_LOGIC_OPERATORS.ANY, QUESTION_LOGIC_OPERATORS.NOT];
const skipOperators = [QUESTION_LOGIC_OPERATORS.ALL, QUESTION_LOGIC_OPERATORS.ANY];

const Question = ({
  firstErrorRef,
  index,
  isDetailedQuestionView,
  number,
  onDuplicateQuestion,
  onEditQuestion,
  onRemoveQuestion,
  provided,
  question,
  questionLogic,
  selectedQuestions,
  setQuestionLogic,
  showActions,
}) => {
  const { currentQuestionCount, instantSurveyType, maxQuestions, survey } =
    useContext(SurveyContext);

  const [isDropdownActive, setIsDropdownActive] = useState(false);
  const [isImageEnlarged, setIsImageEnlarged] = useState(false);
  const isVideoSurvey = instantSurveyType?.id === SURVEY_TYPES.VIDEO;
  const optionsDropdown = useRef(null);

  const addDisqualifyLogic = () => {
    const updatedLogic = { ...questionLogic };
    updatedLogic[question.id] = {
      ...updatedLogic[question.id],
      disqualify: {
        answers: [],
        operator: answerOperators[0],
      },
    };
    setQuestionLogic(updatedLogic);
  };

  const addSkipLogic = () => {
    const updatedLogic = { ...questionLogic };
    updatedLogic[question.id] = {
      ...updatedLogic[question.id],
      skip: {
        logic: [
          {
            answers: [],
            operator: answerOperators[0],
            question: {},
          },
        ],
        operator: skipOperators[0],
      },
    };
    setQuestionLogic(updatedLogic);
  };

  const canAddDisqualifyLogic = useMemo(() => {
    return (
      QUESTION_TYPES_CAN_DISQUALIFY.includes(question.type) &&
      survey.questions
        .slice(0, index)
        .filter(({ type }) => type !== QUESTION_TYPES.INSTRUCTIONS.value).length < 2
    );
  }, [index, question.type, survey]);

  const areAnswersDisplayed = ![
    QUESTION_TYPES.ESSAY.value,
    QUESTION_TYPES.INSTRUCTIONS.value,
    QUESTION_TYPES['TABLE-RADIO'].value,
  ].includes(question.type);

  const hasBadge =
    question.showConceptImage ||
    question?.properties?.randomizeOptions ||
    typeof question?.properties?.maxAnswers === 'number';

  const isInstructionalText = question.type === QUESTION_TYPES.INSTRUCTIONS.value;

  const removeQuestion = () => {
    onRemoveQuestion(question);
    setIsDropdownActive(false);
  };

  return (
    <div
      className={styles['question-draggable-container']}
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
    >
      {questionLogic[question.id]?.skip && (
        <QuestionLogic
          firstErrorRef={firstErrorRef}
          isDetailedQuestionView={isDetailedQuestionView}
          question={question}
          questionIndex={index}
          questionLogic={questionLogic}
          selectedQuestions={selectedQuestions}
          setQuestionLogic={setQuestionLogic}
          type="skip"
        />
      )}

      <Card as={Layout.Flex.Column} className={styles['question-card']} elevation={1} gap="small">
        <Layout.Flex>
          <div className={styles['question-drag-handle-wrapper']}>
            <div className={styles['question-drag-handle']}>
              <DragDropV2Icon />
            </div>
            <div className={styles['question-number']}>{number}</div>
          </div>

          <Layout.Flex.Column className={styles['question-body']}>
            <Layout.Flex gap="small">
              <div className={styles['question-text']}>{question.text}</div>
              <div className={styles['question-pill-wrapper']}>
                <QuestionTypePill className={styles['question-type']} question={question} />
              </div>
            </Layout.Flex>
            {question.image?.cdnUrl && (
              <div className={styles['question-image-wrapper']}>
                <img
                  alt="Uploaded File"
                  className={`${styles['question-image']} ${isImageEnlarged ? styles['question-image-enlarged'] : ''}`}
                  onClick={() => setIsImageEnlarged(!isImageEnlarged)}
                  src={question.image.cdnUrl}
                />
              </div>
            )}
            {isDetailedQuestionView && (
              <div className={styles['question-detailed-view']}>
                {question.type === QUESTION_TYPES.ESSAY.value && (
                  <span className={styles['question-open-ended']}>Panelist's write-in</span>
                )}
                {question.type === QUESTION_TYPES.INSTRUCTIONS.value && (
                  <span className={styles['question-instructional-text']}>
                    This instructional text does not count as a question
                  </span>
                )}
                {question.type === QUESTION_TYPES.NUMERIC.value && (
                  <span className={styles['question-numeric-text']}>
                    {`Panelist's ${getNumericTypeText(question.properties)}`}
                  </span>
                )}
                {question.type === QUESTION_TYPES.VIDEO.value && (
                  <span className={styles['question-video-response']}>
                    Panelist's video response
                  </span>
                )}
                {question.type === QUESTION_TYPES['TABLE-RADIO'].value && (
                  <Layout.Flex className={styles['question-grid']}>
                    <div className={styles['question-grid-rows']}>
                      <Pill icon={<RowsIcon />} isCompleted value="Grid Rows" />
                      <ol>
                        {question.subQuestions.map((subQuestion, index) => (
                          <li key={subQuestion.id}>
                            <strong>{index + 1}</strong>
                            <RowsIcon />
                            {subQuestion.text}
                          </li>
                        ))}
                      </ol>
                    </div>
                    <div className={styles['question-grid-columns']}>
                      <Pill icon={<ColumnsIcon />} isCompleted value="Grid Columns" />
                      <ol>
                        {question.answers.map((answer, index) => (
                          <li key={answer.id}>
                            <strong>{String.fromCharCode(65 + index)}</strong>
                            <ColumnsIcon />
                            {answer.text}
                          </li>
                        ))}
                      </ol>
                    </div>
                  </Layout.Flex>
                )}
                {areAnswersDisplayed && (
                  <>
                    {question.answers
                      .filter(
                        (_, index) => question.type !== QUESTION_TYPES.MAX_DIFF.value || index < 5
                      )
                      .map((answer) => (
                        <Layout.Flex
                          className={styles['question-answer']}
                          gap="small"
                          key={`answer-${answer.id}`}
                        >
                          {getQuestionTypeIcon(
                            question.type === QUESTION_TYPES.TURF.value
                              ? QUESTION_TYPES.CHECKBOX.value
                              : question.type
                          )}
                          <div className={styles['question-answer-body']} gap="x-small">
                            {answer.text && <span>{answer.text}</span>}
                            {answer.image?.cdnUrl && (
                              <img
                                alt="Uploaded File"
                                className={styles['question-answer-image']}
                                src={answer.image.cdnUrl}
                              />
                            )}
                          </div>
                        </Layout.Flex>
                      ))}
                    {question.type === QUESTION_TYPES.MAX_DIFF.value &&
                      question.answers.length > 5 && (
                        <span
                          className={styles['question-more-answers']}
                        >{`or ${question.answers.length - 5} more`}</span>
                      )}
                  </>
                )}
                {hasBadge && (
                  <div className={styles['question-badges']}>
                    {question.showConceptImage && <Pill isCompleted value="Concept Image Shown" />}
                    {question?.properties?.maxAnswers && (
                      <Pill isCompleted value={`Max Answers: ${question.properties.maxAnswers}`} />
                    )}
                    {question?.properties?.randomizeOptions && (
                      <Pill isCompleted value="Randomized Order" />
                    )}
                  </div>
                )}
              </div>
            )}
          </Layout.Flex.Column>

          {showActions && (
            <div className={styles['question-actions']}>
              <ActionOverlayPopover
                hasArrow={false}
                isActive={isDropdownActive}
                onClick={() => setIsDropdownActive(!isDropdownActive)}
                onHide={() => setIsDropdownActive(false)}
                target={optionsDropdown.current}
              >
                <ul>
                  <li>
                    <button
                      aria-label="Edit question"
                      data-testid="edit-question-button"
                      onClick={() => {
                        onEditQuestion(question);
                        setIsDropdownActive(false);
                      }}
                    >
                      <PencilIcon />
                      {isInstructionalText ? 'Edit Text' : 'Edit Question'}
                    </button>
                  </li>
                  {!isVideoSurvey && (
                    <>
                      <li>
                        <button
                          aria-label="Duplicate question"
                          data-testid="duplicate-question-button"
                          disabled={currentQuestionCount >= maxQuestions}
                          onClick={() => {
                            onDuplicateQuestion(question);
                            setIsDropdownActive(false);
                          }}
                        >
                          <CopyIcon />
                          {isInstructionalText ? 'Duplicate Text' : 'Duplicate Question'}
                        </button>
                      </li>
                      <li>
                        <button
                          aria-label="Ask this question if"
                          data-testid="ask-this-question-if-button"
                          disabled={questionLogic[question.id]?.skip}
                          onClick={() => {
                            addSkipLogic();
                            setIsDropdownActive(false);
                          }}
                        >
                          <FlowArrowIcon />
                          {isInstructionalText ? 'Show This Text If' : 'Ask This Question If'}
                        </button>
                      </li>
                      {canAddDisqualifyLogic && (
                        <li>
                          <button
                            aria-label="Disqualify panelists"
                            data-testid="disqualify-panelists-button"
                            disabled={questionLogic[question.id]?.disqualify}
                            onClick={() => {
                              addDisqualifyLogic();
                              setIsDropdownActive(false);
                            }}
                          >
                            <TargetIcon />
                            Disqualify Panelists
                          </button>
                        </li>
                      )}
                    </>
                  )}
                  <hr />
                  <li>
                    <button
                      aria-label="Remove from Survey"
                      data-testid="remove-question-button"
                      onClick={removeQuestion}
                    >
                      <TrashIcon />
                      <span>Remove from Survey</span>
                    </button>
                  </li>
                </ul>
              </ActionOverlayPopover>
            </div>
          )}
        </Layout.Flex>

        {question.type === QUESTION_TYPES['TABLE-RADIO'].value && (
          <GridAnnouncement question={question} />
        )}
        {question.type === QUESTION_TYPES.MAX_DIFF.value && (
          <MaxDiffAnnouncement question={question} />
        )}
      </Card>

      {questionLogic[question.id]?.disqualify && (
        <QuestionLogic
          firstErrorRef={firstErrorRef}
          isDetailedQuestionView={isDetailedQuestionView}
          question={question}
          questionLogic={questionLogic}
          setQuestionLogic={setQuestionLogic}
          type="disqualify"
        />
      )}
    </div>
  );
};

Question.propTypes = {
  firstErrorRef: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  isDetailedQuestionView: PropTypes.bool.isRequired,
  number: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onDuplicateQuestion: PropTypes.func,
  onEditQuestion: PropTypes.func,
  onRemoveQuestion: PropTypes.func,
  provided: PropTypes.object,
  question: PropTypes.object.isRequired,
  questionLogic: PropTypes.object.isRequired,
  selectedQuestions: PropTypes.array.isRequired,
  setQuestionLogic: PropTypes.func.isRequired,
  showActions: PropTypes.bool.isRequired,
};

export default Question;
